import { TextInput } from '@outdoorsyco/bonfire';
import debounce from 'lodash/debounce';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  ELocationSearchAutocompleteListVariant,
  LocationSearchAutocompleteList,
} from '@/components/common/LocationSearchAutocompleteList/LocationSearchAutocompleteList';
import { useLocationSearchAutocompleteOptions } from '@/hooks/useLocationSearchAutocompleteOptions';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { retrieveMapboxFullAddress } from '@/services/retrieveMapboxFullAddress';
import { mapLocationLabelToGroupName, setSurfacedLocation } from '@/utility/surfacedLocation';

import { FilterContent, TFilterContentSharedProps } from './FilterContent';
import { EHomeSearchWidgetVariant } from './HomeSearchWidget';
import { useLocationFilterContext } from './LocationFilterContext';

type TLocationFilterContentProps = TFilterContentSharedProps & {
  onDataSelection?: () => void;
  onTrackFilterToggled?: (
    oldSelection: Record<string, unknown>,
    newSelection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => void;
  variant: EHomeSearchWidgetVariant;
};

export const LocationFilterContent: React.FC<TLocationFilterContentProps> = ({
  onDataSelection,
  onTrackFilterToggled,
  variant,
  ...props
}) => {
  const intl = useIntl();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const {
    addressValue,
    setAddressValue,
    suggestions,
    recentSearches,
    popularDestinations,
    navigateToRecentSearch,
    setMapboxValues,
  } = useLocationFilterContext();

  const debouncedTrackFilterToggledRef = useRef(
    debounce((oldValue: string, newValue: string) => {
      onTrackFilterToggled?.(
        {
          location: oldValue,
        },
        {
          location: newValue,
        },
        ESearchWidgetFilter.Location,
      );
    }, 500),
  );

  const { groupedOptions } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: true,
    suggestions,
    recentSearches,
    popularDestinations,
    includeRecentSearches:
      variant !== EHomeSearchWidgetVariant.AutoRentals &&
      variant !== EHomeSearchWidgetVariant.Escapes,
  });

  const getTitle = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({
        defaultMessage: 'Where to?',
        id: 'XaSWrh',
        description: 'Home Search Widget > Location Modal Title',
      });
    }
    if (variant === EHomeSearchWidgetVariant.RvDelivery) {
      return intl.formatMessage({
        defaultMessage: 'Add a delivery location',
        id: 'nK3TK0',
      });
    } else if (variant === EHomeSearchWidgetVariant.RvRentals) {
      return intl.formatMessage({
        defaultMessage: 'Add a pickup location',
        id: 'nrVolc',
      });
    } else {
      return intl.formatMessage({
        defaultMessage: 'Where to?',
        id: 'XaSWrh',
        description: 'Home Search Widget > Location Modal Title',
      });
    }
  };

  const getLabel = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({
        defaultMessage: 'Location',
        id: 'HJy2Bx',
        description: 'Home Search Widget > Location Filter Label',
      });
    }
    if (variant === EHomeSearchWidgetVariant.RvDelivery) {
      return intl.formatMessage({
        defaultMessage: 'Search a delivery location',
        id: 'AR+AnJ',
      });
    } else if (variant === EHomeSearchWidgetVariant.RvRentals) {
      return intl.formatMessage({
        defaultMessage: 'Pickup location',
        id: 'TudBjU',
      });
    } else {
      return intl.formatMessage({
        defaultMessage: 'Location',
        id: 'rvirM2',
      });
    }
  };

  const handleAddressChange = (value: string) => {
    setAddressValue(value);
    // on address change, debounce the tracking to not spam analytics
    debouncedTrackFilterToggledRef.current?.(addressValue, value);
  };

  const handleSelectOption = async (option: any) => {
    if (!option) return;
    const { label, url, groupName, value } = option;

    if (url) {
      navigateToRecentSearch(option.url);
      return;
    }

    if (value?.mapbox_id) {
      const fullAddress = await retrieveMapboxFullAddress(value);
      setMapboxValues(value.mapbox_id, fullAddress);
    }

    if (value?.name) {
      setAddressValue(value.name);
    } else if (value?.address) {
      setAddressValue(value.address);
    } else if (label) {
      setAddressValue(label);
    }

    setSurfacedLocation(mapLocationLabelToGroupName(groupName));
    onDataSelection?.();

    if (label) {
      onTrackFilterToggled?.(
        {
          location: addressValue,
        },
        {
          location: label,
        },
        ESearchWidgetFilter.Location,
      );
    }
  };

  return (
    <FilterContent
      header={getTitle()}
      content={
        <div className="flex flex-col h-full">
          <TextInput
            controlled
            label={getLabel()}
            inputValue={addressValue}
            onChange={handleAddressChange}
            showClearButton
          />
          <LocationSearchAutocompleteList
            groupedOptions={groupedOptions}
            onSelectOption={handleSelectOption}
            variant={ELocationSearchAutocompleteListVariant.Vertical}
            maxOptionsPerColumn={3}
            maxGroups={2}
          />
        </div>
      }
      {...props}
    />
  );
};
